<template>

  <div
    class="d-flex mb-2"
    :class="{ 'justify-content-end': isOutbound }">

    <div
      class="message-bubble"
      :class="isOutbound ? 'message-bubble-out' : 'message-bubble-in'">

      <!-- author name (only for messages sent by a user) -->
      <div
        v-if="author"
        class="small font-weight-bold text-success mb-1">{{ author }}</div>

      <!-- template header -->
      <div
        v-if="isTemplate"
        class="message-bubble-template small text-muted mb-1 pb-1">
        <i class="fa-solid fa-file-lines mr-1"></i><span class="font-weight-bold">{{ $t('Template') }}:</span> {{ templateName }}
      </div>

      <!-- attached media (image / video / audio / document) -->
      <div
        v-if="media"
        class="message-bubble-media mb-1">
        <img
          v-if="isImage || isSticker"
          :src="media.url"
          class="message-bubble-image rounded">
        <video
          v-else-if="isVideo"
          :src="media.url"
          controls
          class="message-bubble-image rounded"></video>
        <audio
          v-else-if="isAudio"
          :src="media.url"
          controls
          class="message-bubble-audio"></audio>
        <a
          v-else
          :href="media.url"
          target="_blank"
          rel="noopener"
          class="d-flex align-items-center">
          <i class="fa-regular fa-file-lines mr-2"></i>
          <span class="text-truncate">{{ media.title }}</span>
        </a>
      </div>

      <div
        v-if="body"
        class="message-bubble-body">{{ body }}</div>

      <!-- meta line: channel icon + time + delivery status -->
      <div class="message-bubble-meta d-flex align-items-center justify-content-end mt-1 text-muted">
        <i
          v-if="channelIcon"
          :class="channelIcon"
          class="mr-1"></i>
        <span>{{ time }}</span>
        <i
          v-if="isOutbound && statusIcon"
          v-tooltip="statusTooltip"
          :class="statusIcon.class"
          class="ml-1"
          :style="{ color: statusIcon.color }"></i>
      </div>

    </div>

  </div>

</template>

<script>
import moment from 'moment'

export default {
  name: 'MessageBubble',
  props: {
    message: {
      type: Object,
      required: true
    }
  },
  computed: {
    isOutbound () {
      return this.message.direction === 'outbound'
    },
    isTemplate () {
      return this.message.type === 'template'
    },
    // { text } for text, { name, language, body } for template, null for medias
    content () {
      return this.message.content || {}
    },
    templateName () {
      return this.content.name || ''
    },
    // the api populates attachment_media_ids with the media documents
    media () {
      const attachments = this.message.attachment_media_ids || []
      const first = attachments[0]
      return first && first.url ? first : null
    },
    isSticker () {
      return this.message.type === 'sticker'
    },
    isImage () {
      return this.message.type === 'image'
    },
    isVideo () {
      return this.message.type === 'video'
    },
    isAudio () {
      return this.message.type === 'audio'
    },
    body () {
      if (this.isTemplate) {
        return this.content.body || ''
      }
      return this.content.text || this.content.caption || ''
    },
    author () {
      return this.message.sender_user_id ? this.message.sender_user_id.name : null
    },
    time () {
      const date = this.message.sent_at || this.message.created_at
      return date ? moment(date).format('HH:mm') : ''
    },
    channelIcon () {
      switch (this.message.channel_type) {
        case 'whatsapp':
          return 'fab fa-whatsapp'
        case 'email':
          return 'fa-solid fa-envelope'
        case 'sms':
          return 'fa-solid fa-comment-sms'
        default:
          return null
      }
    },
    // icon + color for each delivery state (sending / sent / delivered / read / failed)
    statusIcon () {
      switch (this.message.delivery_status) {
        case 'sending':
          return { class: 'fa-regular fa-clock', color: '#adb5bd', title: this.$t('Sending') }
        case 'sent':
          return { class: 'fa-solid fa-check', color: '#adb5bd', title: this.$t('Sent') }
        case 'delivered':
          return { class: 'fa-solid fa-check-double', color: '#adb5bd', title: this.$t('Delivered') }
        case 'read':
          return { class: 'fa-solid fa-check-double', color: '#2b8aef', title: this.$t('Read') }
        case 'failed':
          return { class: 'fa-solid fa-triangle-exclamation', color: '#e03131', title: this.$t('Failed') }
        default:
          return null
      }
    },
    // why the provider refused the message: { code, message }
    deliveryError () {
      const error = this.message.delivery_error

      return error && (error.code || error.message) ? error : null
    },
    /*
    statusTooltip
    the delivery state on hover, with the provider reason when it failed:
    "Failed · 131047: Message failed to send because more than 24 hours..."
    */
    statusTooltip () {
      if (!this.statusIcon) {
        return null
      }

      if (!this.deliveryError) {
        return this.statusIcon.title
      }

      const detail = [this.deliveryError.code, this.deliveryError.message]
      .filter((part) => { return !!part })
      .join(': ')

      return this.statusIcon.title + ' · ' + detail
    }
  }
}
</script>

<style scoped>
.message-bubble {
  max-width: 72%;
  padding: 8px 10px 6px;
  border-radius: 10px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.06);
  font-size: 14px;
  color: #212529;
}

/* inbound = white, outbound = WhatsApp-like green */
.message-bubble-in {
  background: #fff;
}

.message-bubble-out {
  background: #d6f0d0;
}

.message-bubble-template {
  border-bottom: 1px dashed rgba(0, 0, 0, 0.1);
}

.message-bubble-body {
  white-space: pre-wrap;
  word-break: break-word;
}

.message-bubble-image {
  display: block;
  max-width: 100%;
  max-height: 260px;
}

.message-bubble-audio {
  max-width: 100%;
  height: 36px;
}

.message-bubble-meta {
  font-size: 11px;
}
</style>



// WEBPACK FOOTER //
// src/components/Messaging/thread/MessageBubble.vue